import React, { Suspense, lazy } from "react";
import { createBrowserRouter, RouterProvider, redirect, Outlet, useLocation } from "react-router-dom";
import ErrorPage from "./ErrorPage";
import ErrorBoundary from "../components/ErrorBoundary";
import TabsHelp from "../components/TabsHelp/TabsHelp";
import PageTracker from "../telemetry/PageTracker";
import CookieBanner from "../theme/CookieBanner";
import { FloatingThemeToggle } from "../theme/ThemeToggle";
import { hasValidSession, clearSession, buildLoginUrl } from "../utils/auth/session";

// Help context API. Set DOC_SYNC_HELP_API_URL at build time to override.
const HELP_API_URL =
  process.env.REACT_APP_HELP_API_URL ||
  "https://zsvzaeu172.execute-api.us-east-1.amazonaws.com/help/context";

const HELP_CHAT_URL =
  process.env.REACT_APP_HELP_CHAT_URL ||
  "https://zsvzaeu172.execute-api.us-east-1.amazonaws.com/help/chat";

// Public help site root. The SDK builds per-route deep links of the form
// {root}/web/{slug}, e.g.  /admin/my-business#profile  →
// https://help.keeptabs.app/web/admin-my-business-profile
const HELP_SITE_URL =
  process.env.REACT_APP_HELP_SITE_URL ||
  "https://help.keeptabs.app";

// Routes where the Help panel should NOT mount. These pages have no help docs,
// so mounting TabsHelp there just triggers a 404 on GET /help/context and
// clutters the console. The subscribe / "Choose your Plan" page and the login
// page are pre-auth / transactional and don't use contextual help.
const HELP_DISABLED_PREFIXES = ["/subscription", "/subpart", "/login", "/success"];

const isHelpDisabledPath = (pathname = "") =>
  HELP_DISABLED_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));

// Layout that mounts on every route. TabsHelp uses useLocation() under the
// hood so it MUST live inside the RouterProvider tree, which is why it
// can't go in App.jsx alongside other providers.
const AppLayout = () => {
  const { pathname, search } = useLocation();
  const helpEnabled = !isHelpDisabledPath(pathname);
  // Pages embedded in mytabs.app (ticket checkout ?embed=1, engagement config
  // ?embedded=true) take their theme from ?theme= and show no site chrome.
  const params = new URLSearchParams(search);
  const embedded = params.get("embed") === "1" || params.get("embedded") === "true";
  return (
    <>
      <PageTracker />
      <Outlet />
      {!embedded && <FloatingThemeToggle />}
      {!embedded && <CookieBanner />}
      {helpEnabled && (
        <TabsHelp
          apiUrl={HELP_API_URL}
          chatUrl={HELP_CHAT_URL}
          role="business-owner"
          brand="Help"
          headless
          panelTopOffset={53}
          helpSiteUrl={HELP_SITE_URL}
        />
      )}
    </>
  );
};

// Single page-level loading state for route chunk transitions. Lives inside
// the AppLayout's <Outlet /> Suspense boundary, so it renders within the
// content area only — the sidebar/topbar stay put. Per-view full-page
// spinners are intentionally NOT used; views render their shell immediately
// so the user only ever sees one loader, not a stack.
const PageLoader = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '50vh',
    background: 'var(--ktd-bg, linear-gradient(135deg,#e8f4fd 0%,#dbeeff 35%,#f0f8ff 65%,#e2eeff 100%))',
    padding: '24px',
  }}>
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '16px',
    }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '3px solid var(--ktd-line, #e0e0e0)',
        borderTopColor: '#F09925',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  </div>
);

// Lazy load all views - this splits the bundle into separate chunks
// Auth views (small, load fast)
const LoginView = lazy(() => import("../views/Login/LoginView"));
const RegistrationView = lazy(() => import("../views/Login/RegistrationView"));
const PasswordRecovery = lazy(() => import("../views/Login/PasswordRecovery"));
const ChangePasswordView = lazy(() => import("../views/Login/ChangePasswordView"));
const DeleteAccountView = lazy(() => import("../views/Login/DeleteAccountView"));

// Main layout
const HomeView = lazy(() => import("../views/HomeView"));

// Dashboard views
const Dashboard = lazy(() => import("../views/Dashboards/Dashboard"));
const HomeMainView = lazy(() => import("../views/HomeMain/HomeMainView"));
const AnalyticsView = lazy(() => import("../views/Analytics/AnalyticsView"));

// Business views
const MyBusiness = lazy(() => import("../views/MyBusiness/MyBusiness"));
const ClientCatalogView = lazy(() => import("../views/ClientCatalog/ClientCatalogView"));
const UserCatalogView = lazy(() => import("../views/UserCatalog/UserCatalogView"));

// Event views (heavy - has QR, PDF, etc)
const EventsView = lazy(() => import("../views/Events/EventsView"));
const EventCreate = lazy(() => import("../views/Events/EventCreate"));
const EventCreateNew = lazy(() => import("../views/Events/EventCreateNew"));
const EventEditNew = lazy(() => import("../views/Events/EventEditNew"));

// Subscription views
const SubscriptionView = lazy(() => import("../views/Subscription/SubscriptionView"));
const SubscriptionViewPart = lazy(() => import("../views/Subscription/SubscriptionViewPart"));
const SubscriptionSuccess = lazy(() => import("../views/Subscription/SubscriptionSuccess"));

// Ticket views
const MyTicketsView = lazy(() => import("../views/MyTickets/MyTicketsView"));
const TicketPurchase = lazy(() => import("../views/Tickets/TicketPurchase"));
const TicketSuccess = lazy(() => import("../views/Tickets/TicketSuccess"));
const TicketCancel = lazy(() => import("../views/Tickets/TicketCancel"));

// Organization views
const OrganizationList = lazy(() => import("../views/Organization/OrganizationList"));
const OrganizationDashboard = lazy(() => import("../views/Organization/OrganizationDashboard"));
const CreateOrganization = lazy(() => import("../views/Organization/CreateOrganization"));

// AI Agent views
const AiAgentSubscribe = lazy(() => import("../views/AiAgents/AiAgentSubscribe"));
const AiAgentDashboard = lazy(() => import("../views/AiAgents/AiAgentDashboard"));
const AiAgentDetail = lazy(() => import("../views/AiAgents/AiAgentDetail"));
const AiAgentRouteGuard = lazy(() => import("../components/AiAgentRouteGuard"));
const PlanLevelRouteGuard = lazy(() => import("../components/PlanLevelRouteGuard"));
const SubscriptionGuard = lazy(() => import("../components/SubscriptionGuard"));

// Experience views (admin dashboard)
const ExperiencesDashboard = lazy(() => import("../views/Experiences/ExperiencesDashboard"));
const AllExperiencesDashboard = lazy(() => import("../views/Experiences/AllExperiencesDashboard"));
const ExperienceCatalog = lazy(() => import("../views/Experiences/ExperienceCatalog"));
// Per-experience config/live dispatchers: they read the instance's
// experienceType and render the type-specific screen (Raffle or Live Polls),
// keeping the shared /config and /live routes generic.
const ExperienceConfigRouter = lazy(() => import("../views/Experiences/ExperienceConfigRouter"));
const ExperienceLiveRouter = lazy(() => import("../views/Experiences/ExperienceLiveRouter"));
const DrawingHistory = lazy(() => import("../views/Experiences/DrawingHistory"));
const EntryManagement = lazy(() => import("../views/Experiences/EntryManagement"));
const FulfillmentManagement = lazy(() => import("../views/Experiences/FulfillmentManagement"));
const ExperienceAnalytics = lazy(() => import("../views/Experiences/ExperienceAnalytics"));
const SponsorManagement = lazy(() => import("../views/Experiences/SponsorManagement"));
const PermissionsPanel = lazy(() => import("../views/Experiences/PermissionsPanel"));
const CompliancePanel = lazy(() => import("../views/Experiences/CompliancePanel"));
const DrawComplianceReport = lazy(() => import("../views/Experiences/DrawComplianceReport"));

// Other views
const ShopView = lazy(() => import("../views/Shop/ShopView"));
const SettingsPlatform = lazy(() => import("../views/MyTabsConfiguration/SettingsPlatform"));
const AdminPortal = lazy(() => import("../views/Admin/AdminPortal"));
const BusinessRedirect = lazy(() => import("../views/BusinessRedirect"));
const ServiceLanding = lazy(() => import("../views/ServiceLanding/ServiceLanding"));
const MarketIntelligence = lazy(() => import("../views/ServiceLanding/MarketIntelligence"));
const JumpPage = lazy(() => import("../views/JumpPage/JumpPage"));

// Marketing site views (public for all visitors, including authenticated).
// Route-level code splitting keeps the marketing bundle separate from the dashboard.
const MarketingLayout = lazy(() => import("../views/Marketing/MarketingLayout"));
const Homepage = lazy(() => import("../views/Marketing/Homepage"));
const ProductPage = lazy(() => import("../views/Marketing/products/ProductPage"));
const UseCasePage = lazy(() => import("../views/Marketing/solutions/UseCasePage"));
const MarketingNotFound = lazy(() => import("../views/Marketing/MarketingNotFound"));
const CareersPage = lazy(() => import("../views/Marketing/company/CareersPage"));
const ContactPage = lazy(() => import("../views/Marketing/company/ContactPage"));
const RestaurantsPage = lazy(() => import("../views/Marketing/solutions/RestaurantsPage"));
const PricingPage = lazy(() => import("../views/Marketing/pricing/PricingPage"));

// Wrapper component for lazy-loaded routes
const LazyRoute = ({ children }) => (
  <ErrorBoundary>
    <Suspense fallback={<PageLoader />}>
      {children}
    </Suspense>
  </ErrorBoundary>
);

const routerHandler = (isIntern, allowPass = false) => {
  // Presence of a token is not enough — an expired token must count as logged out,
  // otherwise the authenticated shell renders while every API call fails.
  const _idToken = localStorage.getItem("idToken");
  const isAuthenticated = hasValidSession();

  if (!isAuthenticated && isIntern) {
    // Clear the stale token so we don't bounce between /login and /admin.
    if (_idToken) clearSession();
    return redirect(buildLoginUrl());
  } else if (isAuthenticated && !isIntern) {
    if (allowPass) {
      return false;
    }
    return redirect("/admin/home");
  } else if (_idToken && !isAuthenticated && !isIntern) {
    // Public route reached with an expired token: drop it and let the page render.
    clearSession();
  }
  return false;
};

const router = createBrowserRouter([
  {
    element: <AppLayout />,
    errorElement: <ErrorPage />,
    children: [
      {
        // Public marketing site served at the root for ALL visitors, including
        // authenticated users — no auth loader/guard (Req 19.3). This replaces the
        // former redirect-only loader that bounced visitors to /admin/home or /login.
        path: "/",
        element: <LazyRoute><MarketingLayout /></LazyRoute>,
        children: [
          {
            index: true,
            element: <LazyRoute><Homepage /></LazyRoute>,
          },
          {
            // Alias kept so links shared while the homepage lived here still
            // work. Static segment, so it ranks ahead of `business/:businessId`.
            path: "business",
            element: <LazyRoute><Homepage /></LazyRoute>,
          },
          {
            path: "products/:productSlug",
            element: <LazyRoute><ProductPage /></LazyRoute>,
          },
          {
            path: "careers",
            element: <LazyRoute><CareersPage /></LazyRoute>,
          },
          {
            path: "contact",
            element: <LazyRoute><ContactPage /></LazyRoute>,
          },
          {
            path: "pricing",
            element: <LazyRoute><PricingPage /></LazyRoute>,
          },
          {
            // Bespoke restaurants solutions page — MUST precede the :useCaseSlug
            // param route so "restaurants" is not captured as a generic slug.
            path: "solutions/restaurants",
            element: <LazyRoute><RestaurantsPage /></LazyRoute>,
          },
          {
            path: "solutions/:useCaseSlug",
            element: <LazyRoute><UseCasePage /></LazyRoute>,
          },
        ],
      },
      {
        path: "login",
        element: <LazyRoute><LoginView /></LazyRoute>,
        loader: () => {
          const params = new URLSearchParams(window.location.search);
          const hasReturnUrl = params.has('returnUrl');
          return routerHandler(false, hasReturnUrl);
        },
      },
      {
        path: "password-recovery",
        element: <LazyRoute><PasswordRecovery /></LazyRoute>,
        loader: () => routerHandler(false, true),
      },
      {
        path: "change-password",
        element: <LazyRoute><ChangePasswordView /></LazyRoute>,
        loader: () => routerHandler(true, true),
      },
      {
        path: "delete-account",
        element: <LazyRoute><DeleteAccountView /></LazyRoute>,
        loader: () => routerHandler(false),
      },
      {
        path: "register",
        element: <LazyRoute><RegistrationView /></LazyRoute>,
        loader: () => routerHandler(false),
      },
      {
        path: "subscription",
        element: <LazyRoute><SubscriptionView /></LazyRoute>,
      },
      {
        path: "subpart",
        element: <LazyRoute><SubscriptionViewPart /></LazyRoute>,
      },
      {
        path: "success",
        element: <LazyRoute><SubscriptionSuccess /></LazyRoute>,
      },
      {
        path: "success/session_id=:sessionId",
        element: <LazyRoute><SubscriptionSuccess /></LazyRoute>,
      },
      {
        path: "user/:userId",
        Component: () => {
          window.location.href = 'https://www.mytabs.app';
          return null;
        }
      },
      {
        path: "business/:businessId",
        element: <LazyRoute><BusinessRedirect /></LazyRoute>,
        loader: ({ params }) => params.businessId,
      },
      {
        path: "admin-portal",
        element: <LazyRoute><AdminPortal /></LazyRoute>,
      },
      {
        path: "tickets/:eventId",
        element: <LazyRoute><TicketPurchase /></LazyRoute>,
      },
      {
        path: "tickets/success",
        element: <LazyRoute><TicketSuccess /></LazyRoute>,
      },
      {
        path: "tickets/cancel",
        element: <LazyRoute><TicketCancel /></LazyRoute>,
      },
      {
        path: "b/:code",
        element: <LazyRoute><JumpPage /></LazyRoute>,
      },
      {
        path: "business/ai-agents/subscribe",
        element: <LazyRoute><AiAgentSubscribe /></LazyRoute>,
        loader: () => routerHandler(true),
      },
      {
        path: "m/:code",
        element: <LazyRoute><JumpPage /></LazyRoute>,
      },
      {
        path: "e/:code",
        element: <LazyRoute><JumpPage /></LazyRoute>,
      },
      {
        path: "o/:code",
        element: <LazyRoute><JumpPage /></LazyRoute>,
      },
      {
        path: "/admin",
        element: <LazyRoute><SubscriptionGuard><HomeView /></SubscriptionGuard></LazyRoute>,
        loader: () => routerHandler(true),
        children: [
          {
            path: "dashboards",
            element: <LazyRoute><Dashboard /></LazyRoute>,
            loader: () => routerHandler(true),
          },
          {
            path: "home",
            element: <LazyRoute><HomeMainView /></LazyRoute>,
            loader: () => routerHandler(true),
          },
          {
            path: "client-catalog",
            element: <LazyRoute><ClientCatalogView /></LazyRoute>,
            loader: () => routerHandler(true),
          },
          {
            path: "user-catalog",
            element: <LazyRoute><UserCatalogView /></LazyRoute>,
            loader: () => routerHandler(true),
          },
          {
            path: "my-business",
            element: <LazyRoute><MyBusiness /></LazyRoute>,
            loader: () => routerHandler(true),
          },
          {
            path: "my-business/:businessId",
            element: <LazyRoute><MyBusiness /></LazyRoute>,
            loader: () => routerHandler(true),
          },
          {
            path: "my-events",
            element: <LazyRoute><EventsView /></LazyRoute>,
            loader: () => routerHandler(true),
          },
          {
            path: "experiences",
            // Browse route: open to all paid plans (minLevel 1). Per-engagement
            // gating lives in the catalog (locked card → upgrade modal) + backend
            // create guard, so Starter can browse and discover engagements.
            element: <LazyRoute><PlanLevelRouteGuard minLevel={1} featureName="Tab Engagements across events"><AllExperiencesDashboard /></PlanLevelRouteGuard></LazyRoute>,
            loader: () => routerHandler(true),
          },
          {
            path: "my-events/create",
            element: <LazyRoute><EventCreateNew /></LazyRoute>,
            loader: () => routerHandler(true),
          },
          {
            path: "my-events/create-old",
            element: <LazyRoute><EventCreate /></LazyRoute>,
            loader: () => routerHandler(true),
          },
          {
            path: "my-events/:eventId",
            element: <LazyRoute><EventEditNew /></LazyRoute>,
            loader: () => routerHandler(true),
          },
          {
            path: "my-events/:eventId/experiences",
            // Browse route: open to all paid plans; catalog gates each engagement.
            element: <LazyRoute><PlanLevelRouteGuard minLevel={1} featureName="Event Engagements"><ExperiencesDashboard /></PlanLevelRouteGuard></LazyRoute>,
            loader: () => routerHandler(true),
          },
          {
            path: "my-events/:eventId/experiences/catalog",
            // Browse route: open to all paid plans. The catalog itself locks each
            // engagement per the account's tier (info/upgrade modal); the backend
            // create endpoint enforces the tier as the anti-bypass safety net.
            element: <LazyRoute><PlanLevelRouteGuard minLevel={1} featureName="Event Engagements"><ExperienceCatalog /></PlanLevelRouteGuard></LazyRoute>,
            loader: () => routerHandler(true),
          },
          {
            path: "my-events/:eventId/experiences/:experienceId/config",
            element: <LazyRoute><PlanLevelRouteGuard minLevel={2} featureName="Event Engagements"><ExperienceConfigRouter /></PlanLevelRouteGuard></LazyRoute>,
            loader: () => routerHandler(true),
          },
          {
            path: "my-events/:eventId/experiences/:experienceId/live",
            element: <LazyRoute><PlanLevelRouteGuard minLevel={2} featureName="Event Engagements"><ExperienceLiveRouter /></PlanLevelRouteGuard></LazyRoute>,
            loader: () => routerHandler(true),
          },
          {
            path: "my-events/:eventId/experiences/:experienceId/drawings",
            element: <LazyRoute><PlanLevelRouteGuard minLevel={2} featureName="Event Engagements"><DrawingHistory /></PlanLevelRouteGuard></LazyRoute>,
            loader: () => routerHandler(true),
          },
          {
            path: "my-events/:eventId/experiences/:experienceId/draw-report",
            element: <LazyRoute><PlanLevelRouteGuard minLevel={2} featureName="Event Engagements"><DrawComplianceReport /></PlanLevelRouteGuard></LazyRoute>,
            loader: () => routerHandler(true),
          },
          {
            path: "my-events/:eventId/experiences/:experienceId/entries",
            element: <LazyRoute><PlanLevelRouteGuard minLevel={2} featureName="Event Engagements"><EntryManagement /></PlanLevelRouteGuard></LazyRoute>,
            loader: () => routerHandler(true),
          },
          {
            path: "my-events/:eventId/experiences/:experienceId/fulfillment",
            element: <LazyRoute><PlanLevelRouteGuard minLevel={2} featureName="Event Engagements"><FulfillmentManagement /></PlanLevelRouteGuard></LazyRoute>,
            loader: () => routerHandler(true),
          },
          {
            path: "my-events/:eventId/experiences/:experienceId/analytics",
            element: <LazyRoute><PlanLevelRouteGuard minLevel={2} featureName="Event Engagements"><ExperienceAnalytics /></PlanLevelRouteGuard></LazyRoute>,
            loader: () => routerHandler(true),
          },
          {
            path: "my-events/:eventId/experiences/:experienceId/sponsors",
            element: <LazyRoute><PlanLevelRouteGuard minLevel={2} featureName="Event Engagements"><SponsorManagement /></PlanLevelRouteGuard></LazyRoute>,
            loader: () => routerHandler(true),
          },
          {
            path: "my-events/:eventId/experiences/:experienceId/permissions",
            element: <LazyRoute><PlanLevelRouteGuard minLevel={2} featureName="Event Engagements"><PermissionsPanel /></PlanLevelRouteGuard></LazyRoute>,
            loader: () => routerHandler(true),
          },
          {
            path: "my-events/:eventId/experiences/:experienceId/compliance",
            element: <LazyRoute><PlanLevelRouteGuard minLevel={2} featureName="Event Engagements"><CompliancePanel /></PlanLevelRouteGuard></LazyRoute>,
            loader: () => routerHandler(true),
          },
          {
            path: "analytics",
            element: <LazyRoute><AnalyticsView /></LazyRoute>,
            loader: () => routerHandler(true),
          },
          {
            path: "my-tickets",
            element: <LazyRoute><MyTicketsView /></LazyRoute>,
            loader: () => routerHandler(true),
          },
          {
            path: "shop",
            element: <LazyRoute><ShopView /></LazyRoute>,
            loader: () => routerHandler(true),
          },
          {
            path: "configuration",
            element: <LazyRoute><SettingsPlatform /></LazyRoute>,
            loader: () => routerHandler(true),
          },
          {
            path: "admin-portal",
            element: <LazyRoute><AdminPortal /></LazyRoute>,
            loader: () => routerHandler(true),
          },
          {
            path: "organization",
            element: <LazyRoute><OrganizationList /></LazyRoute>,
            loader: () => routerHandler(true),
          },
          {
            path: "organization/create",
            element: <LazyRoute><CreateOrganization /></LazyRoute>,
            loader: () => routerHandler(true),
          },
          {
            path: "organization/:id",
            element: <LazyRoute><OrganizationDashboard /></LazyRoute>,
            loader: () => routerHandler(true),
          },
          {
            path: "service/market-intelligence",
            element: <LazyRoute><MarketIntelligence /></LazyRoute>,
            loader: () => routerHandler(true),
          },
          {
            path: "service/:serviceId",
            element: <LazyRoute><ServiceLanding /></LazyRoute>,
            loader: () => routerHandler(true),
          },
          {
            path: "ai-agents/subscribe",
            element: <LazyRoute><AiAgentSubscribe /></LazyRoute>,
            loader: () => routerHandler(true),
          },
          {
            path: "ai-agents",
            element: <LazyRoute><AiAgentRouteGuard><Outlet /></AiAgentRouteGuard></LazyRoute>,
            loader: () => routerHandler(true),
            children: [
              {
                index: true,
                element: <LazyRoute><AiAgentDashboard /></LazyRoute>,
              },
              {
                path: "dashboard",
                element: <LazyRoute><AiAgentDashboard /></LazyRoute>,
              },
              {
                path: ":agentId",
                element: <LazyRoute><AiAgentDetail /></LazyRoute>,
              },
            ],
          },
        ],
      },
      {
        // Marketing catch-all: placed LAST so it only matches routes no other
        // top-level route claimed. Renders MarketingNotFound inside MarketingLayout
        // so Nav/Footer stay present (Req 19.10). No auth loader/guard.
        path: "*",
        element: <LazyRoute><MarketingLayout /></LazyRoute>,
        children: [
          {
            index: true,
            element: <LazyRoute><MarketingNotFound /></LazyRoute>,
          },
        ],
      },
    ],
  },
]);

const Router = () => {
  return <RouterProvider router={router} />;
};

export default Router;
